import { HotspotPinComponent as HotspotPincomponentType } from '@lessonup/pins-shared';
import { styled } from '@lessonup/ui-components';
import React from 'react';
import { TipTapComponent } from '../../components/Tiptap/Tiptap.utils';
import { isBoxLayout } from '../../types';
import { computeOpacity, computeShadowFilter } from '../../utils';
import { useEffectivePinComponentSettings, usePinThemeContext } from '../../utils/pinTheme/PinThemeContext';
import { HotspotContentHolder } from './HotspotContentHolder';
import { getHotspotIcon } from './hotspot.utils';

export interface HotspotComponentProps {
  pinId: string;
  pinComponent: HotspotPincomponentType;
  active: boolean;
  Renderer?: TipTapComponent;
}

// These are based how these sizes are relative to the hotspot size in the Figma design
const ICON_SIZE = '57%';
const FONT_SIZE_FACTOR = 18 / 60;

export const HotspotPinComponent: React.FC<HotspotComponentProps> = ({ pinId, pinComponent, active, Renderer }) => {
  const hotspotPosition = isBoxLayout(pinComponent.layout) ? pinComponent.layout.position : { x: 0, y: 0 };
  const hotspotSize = isBoxLayout(pinComponent.layout) ? pinComponent.layout.size : { width: 0, height: 0 };
  const fillType = pinComponent.settings.fillType;
  const fontSize = hotspotSize.height * FONT_SIZE_FACTOR;
  const hotspotSettings = useEffectivePinComponentSettings(pinComponent.type, {
    settings: pinComponent.settings,
  });
  const { typographies } = usePinThemeContext();
  return (
    <>
      <HotspotContentHolder
        showBody={hotspotSettings.showBody || active}
        bodyText={hotspotSettings.body || undefined}
        title={hotspotSettings.title || undefined}
        hotspotSize={hotspotSize}
        hotspotPosition={hotspotPosition}
        expandDirection={hotspotSettings.expandDirection}
        backgroundColor={hotspotSettings.contentBackgroundColor}
        textColor={hotspotSettings.contentTextColor}
        borderColor={hotspotSettings.contentBorderColor}
        shadowSetting={hotspotSettings.shadow}
        pinId={pinId}
        componentId={pinComponent.id}
        Renderer={Renderer}
      />
      <StyledHotspotWrapper
        style={{
          borderWidth: `${hotspotSettings.borderSize}px`,
          borderColor: hotspotSettings.borderColor,
          backgroundColor: hotspotSettings.backgroundColor,
          color: hotspotSettings.innerColor,
          opacity: computeOpacity(hotspotSettings.transparency),
          boxShadow: computeShadowFilter(hotspotSettings.shadow),
          borderRadius: '100%',
          fontFamily: typographies.smallText.fontFamily,
          fontWeight: typographies.smallText.fontWeight,
        }}
      >
        {fillType === 'ICON' && getHotspotIcon(hotspotSettings.iconFill)?.icon}

        {fillType === 'IMAGE' && (
          <StyledImageCore style={{ backgroundImage: `url(${hotspotSettings.imageFill.url})` }} />
        )}
        {fillType === 'TEXT' && (
          <StyledTextWrapper>
            <StyledTextCore
              style={{
                fontSize: fontSize,
              }}
            >
              {hotspotSettings.textFill}
            </StyledTextCore>
          </StyledTextWrapper>
        )}
      </StyledHotspotWrapper>
    </>
  );
};

const StyledHotspotWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  border-style: solid;
  cursor: pointer;

  svg {
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
  }
`;

const StyledTextWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 10%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTextCore = styled.div`
  user-select: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const StyledImageCore = styled.div`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;
